import React from 'react';
import { Link } from 'react-router-dom';

const enterpriseDetails = [
  'Unlimited properties & seats',
  'Custom data retention and refresh SLAs',
  'Dedicated success team & managed onboarding',
  'Priority Slack support and executive reporting'
];

const formatPrice = (amount, currency = 'usd') => {
  if (!Number.isFinite(amount)) {
    return 'Custom pricing';
  }
  if (amount <= 0) {
    return 'Free';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase()
  }).format(amount);
};

const formatRetention = (days) => {
  if (!Number.isFinite(days) || days <= 0) return 'Standard retention';

  if (days >= 365 && days % 365 === 0) {
    const years = Math.round(days / 365);
    return `${years} year${years === 1 ? '' : 's'}`;
  }

  if (days % 30 === 0) {
    const months = Math.round(days / 30);
    return `${months} month${months === 1 ? '' : 's'}`;
  }

  return `${days} day${days === 1 ? '' : 's'}`;
};

const formatRefreshInterval = (hours) => {
  if (!Number.isFinite(hours)) return 'Standard refresh time';
  if (hours <= 0) return 'Continuous refresh';
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `Every ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (hours === 1) return 'Every hour';
  if (hours < 24) return `Every ${hours} hours`;
  if (hours % 24 === 0) {
    const days = Math.round(hours / 24);
    return `Every ${days} day${days === 1 ? '' : 's'}`;
  }
  return `Every ${hours} hours`;
};

const PlanCatalogGrid = ({
  loading,
  planList = [],
  mode = 'billing',
  currentPlanName,
  portalLoading,
  checkoutLoading,
  onManageSubscription,
  onCheckout
}) => {
  if (loading) {
    return (
      <div className="plan-grid">
        <div className="plan-card loading-card">
          <p>Loading plan details…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="plan-grid">
      {planList.map((plan) => {
        const planName = (plan.name || '').toLowerCase();
        const isEnterprise = planName === 'enterprise' || plan.isEnterprise;
        const isCurrentPlan =
          mode === 'billing' && planName && currentPlanName === planName;
        const signupTarget =
          planName === 'free'
            ? '/signup'
            : `/signup${planName ? `?plan=${planName}` : ''}`;

        const monthlyPriceText = formatPrice(plan.monthlyPrice, plan.currency);
        const yearlyPriceText = formatPrice(plan.yearlyPrice, plan.currency);
        const maxSitesText =
          Number.isFinite(plan.maxSites) && plan.maxSites >= 0
            ? plan.maxSites >= 999
              ? 'Unlimited sites'
              : `${plan.maxSites} site${plan.maxSites === 1 ? '' : 's'}`
            : 'Unlimited sites';
        const retentionText = formatRetention(plan.dataRetentionDays);
        const refreshText = formatRefreshInterval(plan.minRefreshIntervalHours);

        return (
          <div
            key={planName || plan.label}
            className={`plan-card ${isCurrentPlan ? 'plan-card-current' : ''}`}
          >
            <span className="plan-badge">{plan.label}</span>
            <p className="plan-description">{plan.description}</p>

            {isEnterprise ? (
              <div className="plan-pricing plan-pricing-enterprise">
                <div className="price-line">
                  <span className="price-amount">Custom pricing</span>
                  <span className="price-interval">Tailored to your footprint</span>
                </div>
              </div>
            ) : (
              <div className="plan-pricing">
                <div className="price-line">
                  <span className="price-amount">{monthlyPriceText}</span>
                  <span className="price-interval">per month</span>
                </div>
                <div className="price-line">
                  <span className="price-amount">{yearlyPriceText}</span>
                  <span className="price-interval">per year</span>
                </div>
              </div>
            )}

            {isEnterprise ? (
              <ul className="plan-details plan-details-enterprise">
                {enterpriseDetails.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            ) : (
              <ul className="plan-details">
                <li>
                  <strong>Sites included:</strong> {maxSitesText}
                </li>
                <li>
                  <strong>Data retention:</strong> {retentionText}
                </li>
                <li>
                  <strong>Refresh time:</strong> {refreshText}
                </li>
                <li>
                  <strong>Billing interval:</strong> Monthly or annual (your choice)
                </li>
                <li>All plans include unified analytics, compliance insights, and revenue reporting.</li>
              </ul>
            )}

            <div className="plan-actions">
              {mode === 'billing' ? (
                isEnterprise ? (
                  <>
                    <Link to="/contact" className="plan-button enterprise">
                      Contact Sales
                    </Link>
                    <p className="plan-enterprise-note">
                      New enterprise partners begin on the free tier, then work with our team to tailor pricing.
                    </p>
                  </>
                ) : isCurrentPlan ? (
                  <button className="plan-button current-plan" disabled>
                    Current Plan
                  </button>
                ) : plan.monthlyPrice <= 0 ? (
                  <button
                    className="plan-button"
                    onClick={() => onManageSubscription?.()}
                    disabled={portalLoading}
                  >
                    Downgrade to Free
                  </button>
                ) : (
                  <div className="plan-upgrade-buttons">
                    <button
                      className="plan-button"
                      onClick={() => onCheckout?.(plan.name, 'monthly')}
                      disabled={checkoutLoading === `${plan.name}-monthly`}
                    >
                      {checkoutLoading === `${plan.name}-monthly` ? 'Launching…' : 'Choose Monthly'}
                    </button>
                    <button
                      className="plan-button secondary"
                      onClick={() => onCheckout?.(plan.name, 'yearly')}
                      disabled={checkoutLoading === `${plan.name}-yearly`}
                    >
                      {checkoutLoading === `${plan.name}-yearly` ? 'Launching…' : 'Choose Yearly'}
                    </button>
                  </div>
                )
              ) : (
                <>
                  {isEnterprise ? (
                    <>
                      <Link to="/contact" className="plan-button enterprise">
                        Contact Sales
                      </Link>
                      <p className="plan-enterprise-note">
                        Already exploring enterprise pricing? Create a free account and message our team from the Support page.
                      </p>
                    </>
                  ) : (
                    <Link to={signupTarget} className="plan-button">
                      {planName === 'free'
                        ? 'Create Free Account'
                        : 'Create Account to Upgrade'}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlanCatalogGrid;


